import React, {FC} from "react"
import {rgba} from "polished"
import styled, {css, withTheme, WithDefaultTheme} from "styled-components"
import {Body} from "@irati/ui/src/Typography"
import {FormattedMessage, injectIntl, InjectIntl} from "@irati/utils/lang"
import {CelebrationTag} from "@irati/types"

import Pill from "@irati/ui/src/Pill"
import mainTheme from "@irati/ui/src/global/theme"

export interface Props extends WithDefaultTheme, InjectIntl {
  selected?: boolean
  tag: CelebrationTag
  onSelect: (tag: CelebrationTag) => void
  variant?: "boxed" | "flat"
  isDefault?: boolean
}

type CSSProps = Prettify<Pick<Props, "variant" | "selected"> & {disabled?: boolean}>

const Container = styled.div<CSSProps>`
  ${({selected, variant, disabled, theme}) => css`
    position: relative;
    user-select: none;
    display: flex;
    height: 100%;
    justify-content: center;
    align-items: center;
    flex-direction: column;
    gap: ${theme.spacing.two};
    ${disabled
      ? css`
          pointer-events: none;
        `
      : css`
          cursor: pointer;
          &:hover {
            border: 2px solid ${selected ? theme.colors.primary2 : theme.colors.primary1};
          }
        `}
    ${variant === "boxed"
      ? css`
          border: 2px solid ${selected ? theme.colors.primary2 : theme.colors.greys.light};
        `
      : css`
          border: 2px solid transparent;
        `}
    width: 170px;
    border-radius: ${theme.spacing.one};
    background-color: ${theme.colors.greys.lightest};
    padding: ${theme.spacing.four} ${theme.spacing.two} ${theme.spacing.two} ${theme.spacing.two};
  `}
`

const RadioContainer = styled.div<CSSProps>`
  ${({selected, theme}) => css`
    position: absolute;
    top: ${theme.spacing.oneAndHalf};
    right: ${theme.spacing.two};
    display: flex;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;
    border: 1px solid ${selected ? theme.colors.primary2 : theme.colors.utility.subdued};
    border-radius: 50%;
    ${Container}:hover & {
      border-color: ${selected ? theme.colors.primary2 : theme.colors.primary1};
    }
  `}
`

const RadioItem = styled.div<CSSProps>`
  ${({selected, theme}) => css`
    width: 12px;
    height: 12px;
    background-color: ${selected ? theme.colors.primary2 : theme.colors.utility.subdued};
    border-radius: 50%;
  `}
`

const DisabledOverlay = styled.div`
  ${({theme}) => css`
    display: flex;
    justify-content: center;
    align-items: center;
    position: absolute;
    height: 100%;
    width: 100%;
    top: 0;
    background-color: ${rgba(theme.colors.greys.grey03, 0.4)};
  `}
`

const PreviewImage = styled.img`
  max-width: 90px;
  max-height: 90px;
`

const TemplateCarouselOption: FC<Props> = ({selected, tag, onSelect, formatMessage, variant, isDefault}) => {
  const {isComingSoon} = tag
  return (
    <Container selected={selected} variant={variant} disabled={isComingSoon} onClick={() => !isComingSoon && onSelect(tag)}>
      {variant === "boxed" && <RadioContainer selected={selected}>{selected && <RadioItem selected={selected} />}</RadioContainer>}
      {tag.icon?.href && <PreviewImage src={tag.icon?.href} alt={tag.icon?.alt} />}
      <Body small textAlign="center" weight={variant === "flat" ? "bold" : "default"}>
        {isDefault ? <FormattedMessage id={tag.name} /> : tag.name}
      </Body>

      {isComingSoon && (
        <DisabledOverlay>
          <Pill
            text={formatMessage({id: "wallet.templates.label.comingSoon"})}
            backgroundColor={mainTheme.colors.greys.grey50}
            isWhiteText
          />
        </DisabledOverlay>
      )}
    </Container>
  )
}

export default withTheme(injectIntl(TemplateCarouselOption))
